// src/services/ValidationService.js - Centralized Validation Logic
import { ErrorTrackingService } from './ErrorTrackingService';
import { SanitizationService } from './SanitizationService';

export class ValidationService {
  
  // Email Validation
  static isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  static normalizeEmail(email) {
    if (!email) return '';
    return email.trim().toLowerCase();
  }

  // Phone Number Validation
  static isValidPhoneNumber(phone) {
    if (!phone || typeof phone !== 'string') return false;
    
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check for valid North American phone number (10 or 11 digits)
    if (digitsOnly.length === 10) {
      return /^[2-9]\d{9}$/.test(digitsOnly); // Area code can't start with 0 or 1
    } else if (digitsOnly.length === 11) {
      return /^1[2-9]\d{9}$/.test(digitsOnly); // Country code 1 + valid area code
    }
    
    return false;
  }

  static normalizePhoneNumber(phone) {
    if (!phone) return '';
    
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    
    return phone; // Return original if can't normalize
  }

  static formatPhoneNumber(phone) {
    if (!phone) return '';
    
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }
    
    return phone; // Return original if can't format
  }

  // Name Validation
  static isValidName(name) {
    if (!name || typeof name !== 'string') return false;
    
    const trimmedName = name.trim();
    return trimmedName.length >= 2 && trimmedName.length <= 100;
  }

  static normalizeName(name) {
    if (!name) return '';
    
    return name.trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/[^\w\s'-]/g, '') // Remove special characters except apostrophes and hyphens
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Business Validation
  static isValidBusinessName(name) {
    if (!name || typeof name !== 'string') return false;
    
    const trimmedName = name.trim();
    return trimmedName.length >= 2 && trimmedName.length <= 100;
  }

  static isValidAddress(address) {
    if (!address || typeof address !== 'string') return false;
    
    const trimmedAddress = address.trim();
    return trimmedAddress.length >= 5 && trimmedAddress.length <= 200;
  }

  // Time Validation
  static isValidTimeFormat(time) {
    if (!time || typeof time !== 'string') return false;
    
    const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timePattern.test(time);
  }

  static isValidTimeRange(timeRange) {
    if (!timeRange || typeof timeRange !== 'string') return false;
    
    if (timeRange === 'closed') return true;
    
    const rangePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]-([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!rangePattern.test(timeRange)) return false;
    
    const [startTime, endTime] = timeRange.split('-');
    return this.isValidTimeFormat(startTime) && 
           this.isValidTimeFormat(endTime) && 
           this.timeToMinutes(startTime) < this.timeToMinutes(endTime);
  }

  static timeToMinutes(time) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  static minutesToTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Price and Number Validation
  static isValidPrice(price) {
    if (price === null || price === undefined) return false;
    
    const numPrice = Number(price);
    return !isNaN(numPrice) && numPrice >= 0 && numPrice <= 10000;
  }

  static isValidDuration(duration) {
    const numDuration = Number(duration);
    return !isNaN(numDuration) && 
           Number.isInteger(numDuration) && 
           numDuration >= 5 && 
           numDuration <= 480; // 8 hours max
  }

  static isValidPercentage(percentage) {
    const numPercentage = Number(percentage);
    return !isNaN(numPercentage) && numPercentage >= 0 && numPercentage <= 100;
  }

  // URL Validation
  static isValidUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  // Date Validation
  static isValidDate(date) {
    if (!date) return false;
    
    const dateObj = new Date(date);
    return dateObj instanceof Date && !isNaN(dateObj.getTime());
  }

  static isValidFutureDate(date) {
    if (!this.isValidDate(date)) return false;
    
    const dateObj = new Date(date);
    const now = new Date();
    return dateObj > now;
  }

  static isValidDateRange(startDate, endDate) {
    if (!this.isValidDate(startDate) || !this.isValidDate(endDate)) return false;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start <= end;
  }

  // Business Logic Validation
  static isValidTier(tier) {
    const validTiers = ['basic', 'premium', 'business', 'elite'];
    return validTiers.includes(tier);
  }

  static isValidBusinessType(type) {
    const validTypes = ['barbershop', 'hair_salon', 'nail_salon', 'spa', 'beauty_studio', 'other'];
    return validTypes.includes(type);
  }

  static isValidTimezone(timezone) {
    if (!timezone || typeof timezone !== 'string') return false;
    
    const validTimezones = [
      'America/Toronto',
      'America/Chicago', 
      'America/Denver',
      'America/Los_Angeles',
      'America/New_York',
      'America/Phoenix',
      'Pacific/Honolulu'
    ];
    
    return validTimezones.includes(timezone);
  }

  // Sanitization Methods
  static sanitizeString(str, maxLength = 255) {
    if (!str || typeof str !== 'string') return '';
    
    return str.trim()
      .slice(0, maxLength)
      .replace(/[<>]/g, ''); // Remove potential HTML tags
  }

  static sanitizeHtml(html) {
    if (!html || typeof html !== 'string') return '';
    
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  // Complex Validation Helpers
  static validateWorkingHours(workingHours) {
    const errors = [];
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    
    if (!workingHours || typeof workingHours !== 'object') {
      return { isValid: false, errors: ['Working hours must be an object'] };
    }
    
    days.forEach(day => {
      if (!workingHours.hasOwnProperty(day)) {
        errors.push(`Missing working hours for ${day}`);
        return;
      }
      
      if (!this.isValidTimeRange(workingHours[day])) {
        errors.push(`Invalid time format for ${day}`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static validateServiceDurations(services) {
    const errors = [];
    
    if (!services || typeof services !== 'object') {
      return { isValid: false, errors: ['Services must be an object'] };
    }
    
    Object.entries(services).forEach(([serviceKey, serviceData]) => {
      if (!serviceData.duration || !this.isValidDuration(serviceData.duration)) {
        errors.push(`Invalid duration for service: ${serviceKey}`);
      }
      
      if (serviceData.price !== undefined && !this.isValidPrice(serviceData.price)) {
        errors.push(`Invalid price for service: ${serviceKey}`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Registration Data Validation
  static validateRegistrationData(registrationData) {
    const errors = [];
    
    // Required fields
    const requiredFields = [
      'businessName', 'businessType', 'phoneNumber', 'address',
      'ownerName', 'ownerEmail', 'selectedPlan'
    ];
    
    requiredFields.forEach(field => {
      if (!registrationData[field]) {
        errors.push(`${field} is required`);
      }
    });
    
    // Field-specific validation
    if (registrationData.ownerEmail && !this.isValidEmail(registrationData.ownerEmail)) {
      errors.push('Valid email address is required');
    }
    
    if (registrationData.phoneNumber && !this.isValidPhoneNumber(registrationData.phoneNumber)) {
      errors.push('Valid phone number is required');
    }
    
    if (registrationData.businessName && !this.isValidBusinessName(registrationData.businessName)) {
      errors.push('Valid business name is required');
    }
    
    if (registrationData.ownerName && !this.isValidName(registrationData.ownerName)) {
      errors.push('Valid owner name is required');
    }
    
    if (registrationData.businessType && !this.isValidBusinessType(registrationData.businessType)) {
      errors.push('Valid business type is required');
    }
    
    if (registrationData.address && !this.isValidAddress(registrationData.address)) {
      errors.push('Valid address is required');
    }
    
    // Working hours validation
    if (registrationData.workingHours) {
      const hoursValidation = this.validateWorkingHours(registrationData.workingHours);
      if (!hoursValidation.isValid) {
        errors.push(...hoursValidation.errors);
      }
    }
    
    // Services validation
    if (registrationData.services) {
      const servicesValidation = this.validateServiceDurations(registrationData.services);
      if (!servicesValidation.isValid) {
        errors.push(...servicesValidation.errors);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Helper method to get validation summary
  static getValidationSummary(validationResult) {
    if (validationResult.isValid) {
      return 'All validation checks passed';
    }
    
    return `Validation failed: ${validationResult.errors.join(', ')}`;
  }

  // Enhanced email validation with sanitization
  static validateAndSanitizeEmail(email) {
    try {
      const sanitized = SanitizationService.sanitizeEmail(email);
      const isValid = this.isValidEmail(sanitized);
      
      return {
        isValid,
        sanitized,
        original: email,
        wasModified: sanitized !== email,
        normalized: this.normalizeEmail(sanitized)
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeEmail',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: email,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced phone validation with sanitization
  static validateAndSanitizePhone(phone) {
    try {
      const sanitized = SanitizationService.sanitizePhoneNumber(phone);
      const isValid = this.isValidPhoneNumber(sanitized);
      
      return {
        isValid,
        sanitized,
        original: phone,
        wasModified: sanitized !== phone,
        normalized: isValid ? this.normalizePhoneNumber(sanitized) : sanitized,
        formatted: isValid ? this.formatPhoneNumber(sanitized) : sanitized
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizePhone',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: phone,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced name validation with sanitization
  static validateAndSanitizeName(name) {
    try {
      const sanitized = SanitizationService.sanitizeName(name);
      const isValid = this.isValidName(sanitized);
      
      return {
        isValid,
        sanitized,
        original: name,
        wasModified: sanitized !== name,
        normalized: isValid ? this.normalizeName(sanitized) : sanitized
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeName',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: name,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced business name validation with sanitization
  static validateAndSanitizeBusinessName(businessName) {
    try {
      const sanitized = SanitizationService.sanitizeBusinessName(businessName);
      const isValid = this.isValidBusinessName(sanitized);
      
      return {
        isValid,
        sanitized,
        original: businessName,
        wasModified: sanitized !== businessName
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeBusinessName',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: businessName,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced address validation with sanitization
  static validateAndSanitizeAddress(address) {
    try {
      const sanitized = SanitizationService.sanitizeAddress(address);
      const isValid = this.isValidAddress(sanitized);
      
      return {
        isValid,
        sanitized,
        original: address,
        wasModified: sanitized !== address
      };
    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeAddress',
        level: 'warning'
      });
      
      return {
        isValid: false,
        sanitized: '',
        original: address,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced registration data validation with sanitization
  static validateAndSanitizeRegistrationData(registrationData) {
    try {
      const sanitized = {};
      const validationResults = {};
      const allErrors = [];
      let hasModifications = false;

      // Field-specific sanitization and validation configurations
      const fieldConfigs = {
        businessName: { method: 'validateAndSanitizeBusinessName' },
        ownerName: { method: 'validateAndSanitizeName' },
        ownerEmail: { method: 'validateAndSanitizeEmail' },
        phoneNumber: { method: 'validateAndSanitizePhone' },
        address: { method: 'validateAndSanitizeAddress' }
      };

      // Process each configured field
      Object.entries(fieldConfigs).forEach(([field, config]) => {
        if (registrationData[field]) {
          const result = this[config.method](registrationData[field]);
          sanitized[field] = result.sanitized;
          validationResults[field] = result;
          
          if (!result.isValid) {
            allErrors.push(`Invalid ${field}: ${result.error || 'validation failed'}`);
          }
          
          if (result.wasModified) {
            hasModifications = true;
          }
        } else {
          sanitized[field] = registrationData[field];
        }
      });

      // Copy other fields as-is but with basic sanitization
      const otherFields = Object.keys(registrationData).filter(
        key => !fieldConfigs.hasOwnProperty(key)
      );

      otherFields.forEach(field => {
        const value = registrationData[field];
        
        if (typeof value === 'string') {
          const basicSanitized = SanitizationService.sanitizeUserInput(value, {
            maxLength: 1000,
            allowSpecialChars: true,
            preventXSS: true,
            preventSQLInjection: true
          });
          
          sanitized[field] = basicSanitized;
          
          if (basicSanitized !== value) {
            hasModifications = true;
          }
        } else {
          sanitized[field] = value;
        }
      });

      // Call original validation on sanitized data
      const originalValidation = this.validateRegistrationData(sanitized);
      allErrors.push(...originalValidation.errors);

      // Enhanced validation for working hours and services
      if (sanitized.workingHours) {
        const hoursResult = this.validateAndSanitizeWorkingHours(sanitized.workingHours);
        if (!hoursResult.isValid) {
          allErrors.push(...hoursResult.errors);
        }
        sanitized.workingHours = hoursResult.sanitized;
        hasModifications = hasModifications || hoursResult.wasModified;
      }

      if (sanitized.services) {
        const servicesResult = this.validateAndSanitizeServices(sanitized.services);
        if (!servicesResult.isValid) {
          allErrors.push(...servicesResult.errors);
        }
        sanitized.services = servicesResult.sanitized;
        hasModifications = hasModifications || servicesResult.wasModified;
      }

      return {
        isValid: allErrors.length === 0,
        errors: allErrors,
        sanitized,
        original: registrationData,
        wasModified: hasModifications,
        fieldResults: validationResults
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeRegistrationData',
        level: 'error'
      });
      
      return {
        isValid: false,
        errors: ['Validation failed due to system error'],
        sanitized: registrationData,
        original: registrationData,
        wasModified: false,
        error: error.message
      };
    }
  }

  // Enhanced working hours validation with sanitization
  static validateAndSanitizeWorkingHours(workingHours) {
    try {
      const sanitized = {};
      const errors = [];
      let wasModified = false;
      const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

      if (!workingHours || typeof workingHours !== 'object') {
        return {
          isValid: false,
          errors: ['Working hours must be an object'],
          sanitized: {},
          original: workingHours,
          wasModified: true
        };
      }

      days.forEach(day => {
        if (workingHours[day]) {
          const originalValue = workingHours[day];
          let sanitizedValue = originalValue;

          // Sanitize time range strings
          if (typeof originalValue === 'string' && originalValue !== 'closed') {
            sanitizedValue = SanitizationService.sanitizeUserInput(originalValue, {
              maxLength: 20,
              allowSpecialChars: true, // Allow colons and hyphens for time format
              preventXSS: true,
              preventSQLInjection: true
            });

            if (sanitizedValue !== originalValue) {
              wasModified = true;
            }
          }

          sanitized[day] = sanitizedValue;

          // Validate the sanitized value
          if (!this.isValidTimeRange(sanitizedValue)) {
            errors.push(`Invalid time format for ${day}: ${sanitizedValue}`);
          }
        } else {
          errors.push(`Missing working hours for ${day}`);
          sanitized[day] = 'closed'; // Safe default
          wasModified = true;
        }
      });

      return {
        isValid: errors.length === 0,
        errors,
        sanitized,
        original: workingHours,
        wasModified
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeWorkingHours',
        level: 'warning'
      });
      
      return {
        isValid: false,
        errors: ['Working hours validation failed'],
        sanitized: {},
        original: workingHours,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced services validation with sanitization
  static validateAndSanitizeServices(services) {
    try {
      const sanitized = {};
      const errors = [];
      let wasModified = false;

      if (!services || typeof services !== 'object') {
        return {
          isValid: false,
          errors: ['Services must be an object'],
          sanitized: {},
          original: services,
          wasModified: true
        };
      }

      Object.entries(services).forEach(([serviceKey, serviceData]) => {
        if (!serviceData || typeof serviceData !== 'object') {
          errors.push(`Invalid service data for ${serviceKey}`);
          return;
        }

        const sanitizedService = {};

        // Sanitize service duration
        if (serviceData.duration !== undefined) {
          const originalDuration = serviceData.duration;
          let sanitizedDuration = originalDuration;

          if (typeof originalDuration === 'string') {
            sanitizedDuration = SanitizationService.sanitizeUserInput(originalDuration, {
              maxLength: 10,
              allowSpecialChars: false, // Only numbers
              preventXSS: true,
              preventSQLInjection: true
            });

            // Convert to number if it's a valid number string
            const numericDuration = parseInt(sanitizedDuration, 10);
            if (!isNaN(numericDuration)) {
              sanitizedDuration = numericDuration;
            }

            if (sanitizedDuration !== originalDuration) {
              wasModified = true;
            }
          }

          sanitizedService.duration = sanitizedDuration;

          if (!this.isValidDuration(sanitizedDuration)) {
            errors.push(`Invalid duration for service: ${serviceKey}`);
          }
        }

        // Sanitize service price
        if (serviceData.price !== undefined) {
          const originalPrice = serviceData.price;
          let sanitizedPrice = originalPrice;

          if (typeof originalPrice === 'string') {
            sanitizedPrice = SanitizationService.sanitizeUserInput(originalPrice, {
              maxLength: 10,
              allowSpecialChars: true, // Allow decimal points
              preventXSS: true,
              preventSQLInjection: true
            });

            // Convert to number if it's a valid number string
            const numericPrice = parseFloat(sanitizedPrice);
            if (!isNaN(numericPrice)) {
              sanitizedPrice = numericPrice;
            }

            if (sanitizedPrice !== originalPrice) {
              wasModified = true;
            }
          }

          sanitizedService.price = sanitizedPrice;

          if (!this.isValidPrice(sanitizedPrice)) {
            errors.push(`Invalid price for service: ${serviceKey}`);
          }
        }

        // Sanitize service name if present
        if (serviceData.name) {
          const originalName = serviceData.name;
          const sanitizedName = SanitizationService.sanitizeUserInput(originalName, {
            maxLength: 100,
            allowSpecialChars: true,
            preventXSS: true,
            preventSQLInjection: true
          });

          sanitizedService.name = sanitizedName;

          if (sanitizedName !== originalName) {
            wasModified = true;
          }
        }

        // Copy other properties as-is
        Object.keys(serviceData).forEach(key => {
          if (!['duration', 'price', 'name'].includes(key)) {
            sanitizedService[key] = serviceData[key];
          }
        });

        sanitized[serviceKey] = sanitizedService;
      });

      return {
        isValid: errors.length === 0,
        errors,
        sanitized,
        original: services,
        wasModified
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeServices',
        level: 'warning'
      });
      
      return {
        isValid: false,
        errors: ['Services validation failed'],
        sanitized: {},
        original: services,
        wasModified: true,
        error: error.message
      };
    }
  }

  // Enhanced form data validation with comprehensive sanitization
  static validateAndSanitizeFormData(formData, options = {}) {
    try {
      const config = {
        strictMode: options.strictMode || false,
        auditSecurity: options.auditSecurity !== false, // Default true
        preserveStructure: options.preserveStructure !== false, // Default true
        maxDepth: options.maxDepth || 3,
        ...options
      };

      // Security audit if enabled
      if (config.auditSecurity) {
        this.auditFormDataSecurity(formData);
      }

      // Recursive sanitization with depth limit
      const sanitizeRecursive = (data, depth = 0) => {
        if (depth > config.maxDepth) {
          return data; // Prevent infinite recursion
        }

        if (Array.isArray(data)) {
          return data.map(item => sanitizeRecursive(item, depth + 1));
        }

        if (data && typeof data === 'object') {
          const sanitizedObj = {};
          Object.entries(data).forEach(([key, value]) => {
            const sanitizedKey = SanitizationService.sanitizeUserInput(key, {
              maxLength: 100,
              allowSpecialChars: false,
              preventXSS: true,
              preventSQLInjection: true
            });
            
            sanitizedObj[sanitizedKey] = sanitizeRecursive(value, depth + 1);
          });
          return sanitizedObj;
        }

        if (typeof data === 'string') {
          return SanitizationService.sanitizeUserInput(data, {
            maxLength: config.strictMode ? 255 : 1000,
            allowSpecialChars: !config.strictMode,
            preventXSS: true,
            preventSQLInjection: true,
            preventPathTraversal: true
          });
        }

        return data; // Numbers, booleans, null, etc.
      };

      const sanitized = sanitizeRecursive(formData);

      return {
        isValid: true,
        sanitized,
        original: formData,
        wasModified: JSON.stringify(sanitized) !== JSON.stringify(formData)
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'validateAndSanitizeFormData',
        level: 'error'
      });
      
      return {
        isValid: false,
        sanitized: formData,
        original: formData,
        wasModified: false,
        error: error.message
      };
    }
  }

  // Security audit for form data
  static auditFormDataSecurity(formData, context = 'form_data') {
    try {
      const findings = [];

      const auditValue = (value, path) => {
        if (typeof value === 'string') {
          const audit = SanitizationService.auditInput(value, `${context}.${path}`);
          if (!audit.safe) {
            findings.push(...audit.findings.map(finding => ({
              ...finding,
              path,
              value: value.substring(0, 50) // Truncate for security
            })));
          }
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            auditValue(item, `${path}[${index}]`);
          });
        } else if (value && typeof value === 'object') {
          Object.entries(value).forEach(([key, val]) => {
            auditValue(val, `${path}.${key}`);
          });
        }
      };

      Object.entries(formData).forEach(([key, value]) => {
        auditValue(value, key);
      });

      if (findings.length > 0) {
        ErrorTrackingService.addBreadcrumb(
          'Security audit findings in form data',
          'security',
          'warning',
          { findingsCount: findings.length, context }
        );

        // Don't log the actual malicious content, just the detection
        ErrorTrackingService.captureError(
          new Error(`Security violations detected in form data: ${findings.length} findings`),
          {
            service: 'ValidationService',
            method: 'auditFormDataSecurity',
            violationTypes: findings.map(f => f.type),
            violationCount: findings.length,
            level: 'warning'
          }
        );
      }

      return {
        safe: findings.length === 0,
        findings,
        auditedFields: Object.keys(formData).length
      };

    } catch (error) {
      ErrorTrackingService.captureError(error, {
        service: 'ValidationService',
        method: 'auditFormDataSecurity',
        level: 'error'
      });
      
      return {
        safe: false,
        findings: [],
        error: error.message
      };
    }
  }

  // Utility method to get sanitization summary
  static getSanitizationSummary(validationResult) {
    if (!validationResult.wasModified) {
      return 'No sanitization required - input was already clean';
    }

    const summary = [];
    
    if (validationResult.fieldResults) {
      Object.entries(validationResult.fieldResults).forEach(([field, result]) => {
        if (result.wasModified) {
          summary.push(`${field}: sanitized`);
        }
      });
    }

    return summary.length > 0 
      ? `Sanitized fields: ${summary.join(', ')}`
      : 'Input was sanitized for security';
  }


}